import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Question, QuestionGroup, QuestionType, Survey } from '../models';

@Injectable()
export class SurveyFormService {
  generalGroup: FormGroup;
  questionGroupsGroup: FormGroup;

  surveyForm: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
  ) {
    this.generalGroup = this.formBuilder.group({
      isEnabled: ['', []],
      topic: ['', [Validators.required]],
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required]],
      organization: ['', [Validators.required]],
    });

    this.questionGroupsGroup = this.formBuilder.group({
      groups: this.formBuilder.array([]),
    });

    this.surveyForm = this.formBuilder.group({
      lang: ['en', [Validators.required]],
      generalGroup: this.generalGroup,
      questionGroupsGroup: this.questionGroupsGroup,
    });
  }

  getFormFromSurvey(survey: Survey): FormGroup {
    this.generalGroup.patchValue(survey);
    this.emptyQuestionGroupsGroup();

    (survey.questionGroups as QuestionGroup[])
      .forEach((questionGroup: QuestionGroup) => {
        const questionGroupFormGroup: FormGroup = this.getNewQuestionGroupGroup();
        questionGroupFormGroup.patchValue(questionGroup);
        (questionGroup.questions as Question[]).forEach(question => {
          const questionFormGroup: FormGroup = this.getNewQuestionGroup();
          questionFormGroup.patchValue(question);
          questionFormGroup.patchValue({type: (question.type as QuestionType).id});
          (questionGroupFormGroup.get('questions') as FormArray).push(questionFormGroup);
        });
        (this.questionGroupsGroup.get('groups') as FormArray).push(questionGroupFormGroup);
      });

    return this.surveyForm;
  }

  getNewQuestionGroup(): FormGroup {
    return this.formBuilder.group({
      id: [''],
      question: ['', [Validators.required]],
      position: ['', [Validators.required]],
      type: ['', [Validators.required]],
      help: [''],
      isMandatory: [''],
      isEnabled: [''],
    });
  }

  getNewQuestionGroupGroup(): FormGroup {
    return this.formBuilder.group({
      id: [''],
      name: ['', [Validators.required]],
      description: [''],
      topic: ['', [Validators.required]],
      position: ['', [Validators.required]],
      questions: this.formBuilder.array([]),
    });
  }

  getQuestionGroupFromForm(form: FormGroup, questionGroup: QuestionGroup = null): QuestionGroup {
    const questions: Question[] = [];
    const questionsArray = form.get('questions') as FormArray;

    if (questionGroup) {
      questionGroup = this.updateObjectProperties(questionGroup, form.value);
      questionsArray.controls.forEach((question: FormGroup) => {
        const found = (questionGroup.questions as Question[])
          .find(handled => +handled.id === +question.controls.id);
        const questionToAdd = found ?
          this.updateObjectProperties(found, question.value) :
          new Question(question.value);

        questions.push(questionToAdd);
      });
    } else {
      questionGroup = new QuestionGroup(form.value);

      questionsArray.controls.forEach((question: FormGroup) => {
        const newQuestion = new Question(question.value);
        const questionTypeId = (newQuestion.type as string).split('/').pop();
        newQuestion.type = {id: questionTypeId} as QuestionType;

        questions.push(newQuestion);
      });
    }

    questionGroup.questions = questions;
    return questionGroup;
  }

  getSurveyFromForm(form: FormGroup, survey: Survey = null): Survey {
    const generalGroup: FormGroup = form.get('generalGroup') as FormGroup;
    const guestionGroupsGroup: FormGroup = form.get('questionGroupsGroup') as FormGroup;

    const groupsArray: FormArray = guestionGroupsGroup.get('groups') as FormArray;
    const questionGroups: QuestionGroup[] = [];

    if (survey) {
      survey = this.updateObjectProperties(survey, generalGroup.value);

      groupsArray.controls.forEach((group: FormGroup) => {
        const found = (survey.questionGroups as QuestionGroup[])
          .find(handled => +handled.id === +group.controls.id);
        const questionGroupToAdd = found ?
          this.getQuestionGroupFromForm(group, found) :
          this.getQuestionGroupFromForm(group);
        questionGroups.push(questionGroupToAdd);
      });
    } else {
      survey = new Survey(generalGroup.value);
      survey.organization = '/api/organizations/'+generalGroup.get('organization').value.id;

      groupsArray.controls.forEach((group: FormGroup) => {
        questionGroups.push(this.getQuestionGroupFromForm(group));
      });
    }

    survey.questionGroups = questionGroups;

    return survey;
  }

  /**
   * Empty the formArray this.questionGroupsGroup.groups preserving subscription
   */
  private emptyQuestionGroupsGroup() {
    while ((this.questionGroupsGroup.controls.groups as FormArray).length) {
      (this.questionGroupsGroup.controls.groups as FormArray).removeAt(0);
    }
  }

  /**
   * function to overwite properties of an object without destroying others
   *
   * @param obj1 The object to update
   * @param obj2 The object containing values to pass to obj1
   * @return
   */
  private updateObjectProperties(obj1: any, obj2: any): any {
    Object.keys(obj2).forEach(key => {
      if (key === 'type') {
        obj1['type']['id'] = obj2[key];
      } else if ((key in obj1)) {
        obj1[key] = obj2[key];
      }
    });
    return obj1;
  }

}
